'use client'

import { Doctor } from '@/lib/types/doctor'
import { useState } from 'react'
import SectionTabs from './SectionTabs'

interface Review {
  id: number
  consultation: number | null
  comment: string
  rating: number
  created: string
  patient_name: string
}

interface ReviewsData {
  prescriber: {
    id: number
    name: string
    rating: number
  }
  reviews: Review[]
}

interface TabContentProps {
  doctor: Doctor
  reviewsData: ReviewsData | null
}

export default function TabContent({ doctor: data, reviewsData }: TabContentProps) {
  const [activeTab, setActiveTab] = useState('about')

  return (
    <>
      {/* Tab Navigation */}
      <SectionTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      <div className=''>
        <div className={`m-0 flex flex-col gap-6`}>
          {/* About Tab */}
          {activeTab === 'about' && (
            <div>
              <p className='whitespace-pre-wrap text-base font-normal'>{data?.about}</p>
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className='w-ful'>
              {/* SERVICES HEADER */}
              <div className='mb-4 grid w-full grid-cols-3 gap-4 md:gap-20'>
                <h2 className='text-start text-base font-semibold md:text-xl'>Services</h2>
                <h2 className='text-center text-base font-semibold md:text-xl'>Duration</h2>
                <h2 className='text-right text-base font-semibold md:text-xl'>Fees</h2>
              </div>

              {/* SERVICES LIST */}
              <div className='flex flex-col gap-2 text-base font-normal'>
                {(data?.treatments ?? []).length > 0 ? (
                  (data?.treatments ?? []).map((treatment) => (
                    <div key={treatment.id} className='grid w-full grid-cols-3 gap-4 text-sm md:gap-0 md:text-base'>
                      <p className='text-start'>{treatment.treatment_display}</p>
                      <p className='text-center'>{treatment.duration} Min</p>
                      <p className='text-right'>£ {treatment.fee}</p>
                    </div>
                  ))
                ) : (
                  <p>No services found.</p>
                )}
              </div>

              {/* CONDITIONS + FOLLOW UP */}
              <div className='mt-10 grid w-full grid-cols-1 gap-8 pr-0 md:grid-cols-2 md:gap-0 md:pr-20'>
                {/* CONDITIONS */}
                <div>
                  <h2 className='text-xl font-semibold mb-4'>Conditions Treated</h2>
                  <div className='flex flex-col gap-2 text-base font-normal'>
                    {(data?.conditions_display ?? []).length > 0 ? (
                      (data?.conditions_display ?? []).map((item: string) => (
                        <div key={item} className='flex items-start gap-2'>
                          <span>→</span>
                          <p>{item}</p>
                        </div>
                      ))
                    ) : (
                      <p>No conditions found.</p>
                    )}
                  </div>
                </div>

                {/* FOLLOW UP */}
                <div>
                  <h2 className='text-xl font-semibold mb-4'>Follow Up Fee</h2>
                  <p className='text-base font-normal'>Started from</p>
                  <p className='text-base font-normal mt-1'>£ {data?.fee?.follow_up_fee ?? 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Qualifications Tab */}
          {activeTab === 'qualification' && (
            <div className='w-full rounded-md bg-gray-100 p-4 md:p-6'>
              {/* HEADER */}
              <div className='mb-3 grid grid-cols-2 gap-4 md:gap-0'>
                <h2 className='text-base font-semibold md:text-lg'>Degree / Courses</h2>
                <h2 className='text-right text-base font-semibold md:text-lg'>Year of Completion</h2>
              </div>

              {/* LIST */}
              <div className='flex flex-col gap-1 text-base'>
                {(data?.qualifications ?? []).length > 0 ? (
                  (data?.qualifications ?? []).map((qualification) => (
                    <div key={qualification.id} className='grid grid-cols-2 gap-4 md:gap-0'>
                      <p>{qualification.course}</p>
                      <p className='text-right'>{qualification.graduation_date || '-'}</p>
                    </div>
                  ))
                ) : (
                  <p>No qualifications found.</p>
                )}
              </div>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className=''>
              {/* Total Rating */}
              {/* {reviewsData && reviewsData?.reviews?.length > 0 && (
                                <div className='mb-6 flex items-center gap-2'>
                                    {(() => {
                                        const avg = reviewsData.prescriber?.rating?.toFixed(1) ?? '0.0'
                                        return (
                                            <>
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <span
                                                        key={star}
                                                        className={
                                                            star <= Math.round(Number(avg))
                                                                ? 'text-2xl text-yellow-500'
                                                                : 'text-2xl text-gray-300'
                                                        }
                                                    >
                                                        ★
                                                    </span>
                                                ))}
                                                <span className='ml-2 text-base font-light'>({avg}/5)</span>
                                            </>
                                        )
                                    })()}
                                </div>
                            )} */}
              <div className='grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3'>
                {reviewsData && reviewsData?.reviews?.length > 0 ? (
                  reviewsData.reviews.map((review: Review) => (
                    <div key={review.id} className='rounded-lg bg-white p-5 flex flex-col h-full'>
                      {/* COMMENT */}
                      <p className='text-base font-normal pb-5 break-words whitespace-pre-wrap'>
                        {review.comment || '...'}
                      </p>

                      {/* BOTTOM SECTION */}
                      <div className='mt-auto'>
                        <span className='font-medium text-base'>{review.patient_name}</span>
                        <span className='flex items-center gap-1 -mt-1'>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              className={
                                star <= review.rating ? 'text-base text-[#FFC72D]' : 'text-base text-[#D9D9D9]'
                              }
                            >
                              ★
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div>No reviews found.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
